import type { Transition, Variants } from 'motion/react';

/**
 * Hareket token'ları — DESIGN.md §2'nin kod karşılığı.
 *
 * Bileşenlerde ham sayı YAZILMAZ. Yeni bir değere ihtiyaç duyuluyorsa önce
 * DESIGN.md'ye eklenir, sonra buraya; tersi olursa dokümanla kod ayrışır.
 *
 * Birim notu: DESIGN.md süreleri milisaniye tablosuyla veriyor (120, 220…),
 * Motion ise saniye bekliyor. Burada saniye tutuluyor — çeviriyi her çağrı
 * yerinde tekrarlamak yerine tek yerde yapmak için.
 */

/* --- Yaylar. Konum / ölçek / boyut değişimi HER ZAMAN spring. ------------ */
export const spring = {
  /** Buton basma, toggle, küçük ve anlık şeyler */
  snap: { type: 'spring', stiffness: 400, damping: 22, mass: 0.6 },
  /** Kart girişi, panel açılışı — çoğu yer */
  default: { type: 'spring', stiffness: 260, damping: 28, mass: 0.9 },
  /** Büyük yüzeyler, modal, sayfa geçişi */
  soft: { type: 'spring', stiffness: 160, damping: 30, mass: 1.1 },
  /** Başarı durumu, kutlama anı */
  bouncy: { type: 'spring', stiffness: 320, damping: 18, mass: 0.8 },
} satisfies Record<string, Transition>;

/* --- Süreler. Yalnızca tween için: renk, opaklık, blur. ------------------ */
export const dur = {
  instant: 0.12,
  fast: 0.22,
  base: 0.35,
  slow: 0.6,
  ambient: 1.2,
} as const;

/* --- Easing. Sonsuz döngü dışında 'linear' kullanılmaz. ------------------ */
export const ease = {
  /** giriş — hızlı başla, yumuşak dur */
  out: [0.22, 1, 0.36, 1],
  /** çıkış */
  in: [0.64, 0, 0.78, 0],
  /** yer değiştirme */
  inOut: [0.83, 0, 0.17, 1],
} as const;

/* --- Stagger. eleman sayısı × değer > 1.2 sn ise bir tık düşür. ---------- */
export const stagger = {
  /** 8+ elemanlı listeler */
  tight: 0.04,
  /** kart ızgarası, menü satırları */
  base: 0.08,
  /** 3–4 elemanlı kahraman bölümü */
  loose: 0.14,
} as const;

/**
 * Konum/ölçek yay ile, opaklık ve blur tween ile — DESIGN.md §2'nin
 * "spring yalnızca fiziksel karşılığı olan şeyler için" kuralı.
 *
 * Neden gerekli: tek bir spring tüm özelliklere uygulanınca opaklık da yay
 * eğrisiyle ilerliyor ve sona yaklaşırken asimptotik olarak sürünüyor. Panel
 * yerine oturmuş görünürken hâlâ %92 opak kalıyor, altındaki içerik siliniyor.
 * Mobil çekmecede bu "arka plan şeffaf kalmış" gibi görünüyordu.
 */
export function springFade(s: Transition, fade: number = dur.fast): Transition {
  return {
    ...s,
    opacity: { duration: fade, ease: ease.out },
    filter: { duration: fade, ease: ease.out },
  };
}

/**
 * Sahne girişi — DESIGN.md §3 K1'in paylaşılan hâli.
 *
 * `blur` bu setin imzası: yalnız opacity+y ile giriş jenerik duruyor, blur
 * eklenince "odaklanıyor" hissi veriyor. Bütçesi var (§6): aynı anda ~15
 * öğeden fazlasında kullanma, uzun listelerde blur'suz varyanta geç.
 */
export const sceneContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
  /* Çıkışta sıra ters: en alttaki önce gider, menü "toplanıyor" gibi durur. */
  exit: { transition: { staggerChildren: stagger.tight, staggerDirection: -1 } },
};

export const sceneItem: Variants = {
  hidden: { opacity: 0, y: 12, filter: 'blur(6px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, y: 6, filter: 'blur(4px)', transition: { duration: dur.instant } },
};

/**
 * İskelet maket parçaları — `sceneItem`'ın blur'suz ve kısa yollu hâli.
 *
 * Neden ayrı bir set: bir maket 8–12 küçük parçadan oluşuyor (çubuklar,
 * kartlar, düğümler). `sceneItem`'ın 12px'lik yolu bu ölçekte "fırlıyor" gibi
 * duruyor, blur'u ise §6'daki ~15 öğelik bütçeyi tek başına tüketiyor.
 * Değerler yeni değil, mevcut token'ların başka bir bileşimi.
 */
export const mockContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: stagger.tight } },
  exit: { transition: { staggerChildren: stagger.tight, staggerDirection: -1 } },
};

export const mockItem: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: dur.fast, ease: ease.out } },
  exit: { opacity: 0, y: -4, transition: { duration: dur.instant } },
};

/* --- Tablo satırlarının oturma dalgası ------------------------------------
   Paneldeki İKİ tablo da (kampanyalar, otomasyon kuralları) bunu kullanıyor:
   aynı deriyi giyen iki liste, sıralandığında aynı şekilde yerine oturmalı.

   Ne zaman koşuyor: tetikleyici `tbody`nin `key`i, yani onu KİM kullanıyorsa
   o karar veriyor.
   - Kampanya tablosu yalnız sıralamada: arama metni imzada YOK, çünkü orada
     filtreleme tuş başına oluyor ve her vuruşta yeniden dizilen bir liste
     yazarken göz takip edemiyor.
   - Kural tablosu adresin tamamında (arama gecikmeli olduğu için tuş başına
     değil, elin durakladığı yerde bir kez): orada liste sunucudan geliyor ve
     dalga "yeni liste geldi" demenin kendisi.

   DESIGN.md §4 tablolar için "ölçülü" diyor; bu yüzden dalga kısa ve gecikme
   sekizinci satırda donuyor (aşağıya bakınız).

   Satır tamamen kaybolup geri gelmiyor (0 değil 0,35'ten başlıyor): sıralama
   satır SİLMİYOR, yalnızca yerini değiştiriyor. Sıfırdan gelseydi tablo bir
   kare boş kalır, "veri yeniden yükleniyor" gibi okunurdu.

   Blur YOK — sahne girişlerinin (`sceneItem`) imzası ama burada bedeli var:
   motion bitişte `filter: blur(0px)`'i satırın üstünde BIRAKIYOR, `none`'a
   dönmüyor. Sıfır bulanıklık bile satırı kalıcı olarak ayrı bir yığın
   bağlamına (çoğu zaman ayrı bir katmana) taşır; yatay kayan ve yapışkan sütun
   taşıyan bir tabloda bu, Chrome'un kaydırma sırasında satırı yeniden boyamayı
   atlamasına açık bir yapı. Ölçüldü: 390px'te bir karede satırların yapışkan
   olmayan hücreleri boş boyandı. Dalga zaten opaklıkla okunuyor. */
export const tableSettleBody: Variants = {
  hidden: {},
  show: {},
};

/**
 * Gecikme `staggerChildren` ile DEĞİL satırın kendi sırasıyla veriliyor
 * (`custom={index}`), çünkü SEKİZİNCİ satırda donuyor.
 *
 * Sebep ölçüldü: kural listesi sayfa başına 25 satır ve her engelli kural bir
 * satır daha ekliyor. `staggerChildren` ile 30 satır × 0,04 sn = 1,2 sn, yani
 * DESIGN.md §6'nın sınırının tam üstü — son satır neredeyse bir buçuk saniye
 * sonra geliyor. Sekizde durdurunca dalga hâlâ okunuyor, bekleme 0,32 sn'de
 * bitiyor. Kampanya tablosunda yedi satır var, orada sınır zaten hiç
 * yakalanmıyor; iki tablo yine de aynı kuralı kullanıyor.
 */
export const tableSettleRow: Variants = {
  hidden: { opacity: 0.35, y: -6 },
  show: (index: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { ...springFade(spring.snap), delay: Math.min(index, 8) * stagger.tight },
  }),
};

/* --- Galeri kutusu (üretim geçmişi) --------------------------------------
   Tablo satırının (`tableSettleRow`) DEĞİL kendi jesti var ve fark bilinçli:
   satır YUKARIDAN oturuyor (`y: -6`, opaklık 0,35'ten) çünkü bir listenin
   içine düşüyor; galeri kutusu AŞAĞIDAN ve hafifçe büyüyerek geliyor, yani
   "beliriyor". İkisi aynı olsaydı bir fotoğraf ızgarası bir tablo gibi
   okunurdu.

   Gecikme yine `custom={index}` ile, `staggerChildren` ile DEĞİL — sebep
   `tableSettleRow`da yazılı ve burada daha da geçerli: sayfa başına 24 kutu
   var, 24 × 0,04 = 0,96 sn ve sınır 1,2 sn (DESIGN.md §6). Tavan 10'da:
   dalga hâlâ okunuyor, son kutu 0,4 sn'de yerinde.

   Blur YOK, aynı gerekçeyle: kutu bir `<img>` taşıyor ve bitişte üstünde
   kalan `filter: blur(0px)` onu kalıcı olarak ayrı bir katmana taşırdı —
   yirmi dört ayrı katman, hepsi tam çözünürlükte fotoğraf. */
export const galleryTile: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  show: (index: number = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { ...springFade(spring.default), delay: Math.min(index, 10) * stagger.tight },
  }),
  /* Çıkış girişin aynası DEĞİL: kutu silindiğinde yerine geri gelmiyor, o
     yüzden kısa bir tween ve içeri doğru küçülme — "buradan kalktı" diyor.
     Komşuların boşluğu kapatması `layout` prop'unda, burada değil. */
  exit: { opacity: 0, scale: 0.94, transition: { duration: dur.fast, ease: ease.in } },
};

/* --- Açılır katman: panel, liste, madde ---------------------------------
   Paneldeki BÜTÜN açılır katmanlar bunu kullanıyor: filtre seçicisi
   (`DashSelect`) ve satır menüsü (`DashMenu`). Aynı jest, aynı süre, aynı
   yön — kullanıcı ekranda tek bir "katman açılıyor" davranışı görüyor.

   Panel düğmenin üst kenarından iniyor; `scaleY` ile açılmıyor çünkü köşe
   yarıçapı ve metin o sırada eziliyor (ölçüldü: 12px'lik yarıçap açılışın
   ortasında 20px gibi duruyor). Tekdüze 0,97'lik ölçek yarıçapı bozmuyor.

   Maddeler açılışta yukarıdan aşağı, kapanışta AŞAĞIDAN YUKARI sıralanıyor
   (`staggerDirection: -1`): katman düğmeye doğru toplanıyor. */
export const dropPanel: Variants = {
  hidden: { opacity: 0, y: -6, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: springFade(spring.snap) },
  exit: { opacity: 0, y: -4, scale: 0.985, transition: { duration: dur.instant, ease: ease.in } },
};

export const dropList: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: dur.instant / 2 } },
  exit: { transition: { staggerChildren: stagger.tight, staggerDirection: -1 } },
};

/* Madde satırın BAŞINDAN kayıyor: sağdan sola dilde başlangıç sağda. İşaret
   `custom` ile geliyor (`useInlineSign()` → `custom={sign}`); verilmeyen
   çağıranda varsayılan `1` — soldan sağa davranış birebir aynı kalıyor.
   Motion her öğenin KENDİ `custom`ını okuyor (üstten miras yok), bu yüzden
   değer `variants={dropItem}` taşıyan her öğeye ayrı yazılıyor. */
export const dropItem: Variants = {
  hidden: (sign: number = 1) => ({ opacity: 0, x: -8 * sign }),
  show: { opacity: 1, x: 0, transition: springFade(spring.snap) },
  exit: (sign: number = 1) => ({
    opacity: 0,
    x: -4 * sign,
    transition: { duration: dur.instant, ease: ease.in },
  }),
};

/* --- Sağdan giren sonuç kartı (toast) -------------------------------------
   `components/ui/toast.tsx`. İKİ set, çünkü tek bir öğe ikisini birden
   yapamıyor: kart hem destede yer değiştiriyor (üstüne yenisi gelince yukarı
   kayıyor → `layout`) hem de sağdan giriyor (`x`). Motion'ın `layout`ı öğenin
   transform'unu KENDİ yönetiyor; aynı öğeye elle `x` verilirse giriş ile
   deste kayması aynı kanalda çakışıyor ve kart yerine oturmadan zıplıyor.

   Bu yüzden dış kabuk (`toastShell`) yalnız opaklık + `layout`, iç kart
   (`toastSlide`) yalnız kayma. Varyant adları aynı olduğu için iç kart
   dıştakinden miras alıyor; `animate` prop'u yazılmıyor.

   Yol YÜZDE ile (`110%`): kart genişliği ekrana göre değişiyor (390px'te
   ~366px, 2560px'te 480px) ve piksel bir değer dar ekranda kartı yarı yolda
   bırakırdı. Yüzde her genişlikte "ekranın sağından geldi" diyor.

   Çıkış girişin aynası DEĞİL — daha kısa ve tween: giriş bir haber veriyor,
   çıkış yalnızca temizlik. Yayla çıkarsa kart giderken bir kez daha dikkat
   çekiyor (DESIGN.md §4 "ağırlık anlam taşır").

   ⚠️ Azaltılmış harekette `MotionConfig reducedMotion="user"` transform'ları
   düşürüyor: kart kaymadan, yalnız opaklıkla beliriyor. Koşullu render YOK
   (bu depoda hidrasyonu kıran tuzak). */
export const toastShell: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: dur.fast, ease: ease.out } },
  exit: { opacity: 0, transition: { duration: dur.fast, ease: ease.in } },
};

/* Deste satırın SONUNA yapışık (`.toast-viewport` → `inset-inline-end`):
   sağdan sola dilde sol altta duruyor ve oradan girmeli. İşaret `custom`
   ile (`useInlineSign()`); verilmezse `1` — soldan sağa davranış aynı. */
export const toastSlide: Variants = {
  hidden: (sign: number = 1) => ({ x: `${110 * sign}%`, scale: 0.98 }),
  show: { x: '0%', scale: 1, transition: spring.default },
  exit: (sign: number = 1) => ({
    x: `${110 * sign}%`,
    scale: 0.98,
    transition: { duration: dur.fast, ease: ease.in },
  }),
};

/* --- Tarama çizgisi -------------------------------------------------------
   Bir yüzeyin "kalibre olduğunu" söyleyen jest: saç teli kalınlığında bir
   çizgi yüzeyin tepesinden dibine bir kez süpürüyor. Açılan seçici katmanı ve
   tazelenen kural tablosu AYNI hareketi kullanıyor — ikisi de aynı soruya
   cevap veriyor: "içerik değişti, şu an yeniden okundu".

   Taşıma YÜZDE ile: kap her zaman yüzeyin tamamı ve `y: 100%` kabın KENDİ
   yüksekliği kadar iniyor. Böylece panelin ya da tablonun boyu hiç ölçülmüyor;
   satır sayısı değişince ayar gerekmiyor.

   Kap `overflow: hidden` olan bir yüzeyde çizgiyi dipte kesiyor; olmayan
   yüzeyde opaklık zaten sıfıra iniyor.

   ⚠️ Azaltılmış harekette taşıma kapalı kalacağı için çizgi tepede yanıp
   sönerdi. Kapatma CSS'te (`display: none`), koşullu render ile DEĞİL:
   `useReducedMotion()` sunucuda ve tarayıcıda farklı ağaç üretip hidrasyonu
   kırıyor (bu depoda yaşanmış tuzak). */
export const scanSweep: Variants = {
  hidden: { y: '0%', opacity: 0 },
  show: {
    y: '100%',
    opacity: [0, 1, 0],
    transition: { duration: dur.base, ease: ease.out },
  },
  exit: { opacity: 0, transition: { duration: dur.instant } },
};
